import { getResourceEntity } from 'js/lib/PipelineUtils';
import { getConnectionOrVendorType, isPublicDataset } from 'js/lib/ResourceUtils';
import { has, map, reduce } from 'lodash';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

const flowsSelector = createSelector(
  [
    (state: any) => state.pipelines,
    (state: any) => state.entities,
    (state: any) => state.paging.metaInfoById,
    (state: any) => state.metrics.dashboard.data_sources,
  ],
  (pipelines, entities, metaInfoById, data_sources) => {
    return {
      pipelines,
      entities,
      metaInfoById,
      data_sources,
    };
  },
);

export function useFlows() {
  const { pipelines: p, entities, metaInfoById, data_sources } = useSelector(flowsSelector);
  const { rootNodeKeys, branches } = p;

  const { flowNodes, users, orgs, vendors } = entities;
  const pipelines = map(rootNodeKeys, (nodeKey) => getResourceEntity(flowNodes[nodeKey], entities));

  return pipelines.map(
    mapItem({
      entities,
      metaInfoById,
      data_sources,
      users,
      orgs,
      branches,
      vendors,
    }),
  );
}

const mapItem =
  ({ entities, branches, vendors, metaInfoById, orgs, data_sources, users }) =>
  (item, idx) => {
    const { flowNodes } = entities;
    const isDataSetCard = item?.type === 'DATA_SET';
    const isSharedDataSet = isDataSetCard && item?.dataSetType === 'shared';
    const data = getResourceEntity(item, entities);
    const branch = branches[data.nodeKey];
    let connectionInfo = null,
      connectionOrVendorType = null;
    let errorCount = 0;
    const branchItems = map(branch && branch.items, (nodeKey) => flowNodes[nodeKey]);
    const childrenCount = reduce(
      branchItems,
      (acc, thisItem, idx) => {
        if (thisItem.type === 'DATA_SET' && thisItem.dataSetType === 'derived') {
          acc.derivedDataSetsCount += 1;
        } else if (thisItem.type === 'DATA_SINK') {
          acc.sinksCount += 1;
        }
        return acc;
      },
      {
        derivedDataSetsCount: 0,
        sinksCount: 0,
      },
    );

    if (!isDataSetCard) {
      connectionOrVendorType = getConnectionOrVendorType(data, vendors, 'source_type', 'vendor_id');

      connectionInfo =
        metaInfoById && has(metaInfoById, connectionOrVendorType)
          ? metaInfoById[connectionOrVendorType]
          : null;
      errorCount = has(data_sources, `${data.id}.errors`) ? data_sources[data.id].errors : 0;
    }

    const isPublic = isPublicDataset(data);
    let sharedByOrg = null,
      sharedByUser = null;

    if (isSharedDataSet && isPublic === false) {
      sharedByUser = has(data, 'owner_id') ? users[data.owner_id] : null;
      sharedByOrg = has(data, 'org_id') ? orgs[data.org_id] : null;
    }

    return {
      source: connectionInfo,
      connectionOrVendorType,

      // TODO: Do we need to return all the things aside from data? Are the other
      // returned properties being used anywhere?
      data,
      sourceDataSetsCount: data?.children?.length || 0,
      childrenCount,
      errorCount,
      isDataSetCard,
      isPublic,
      sharedByUser,
      sharedByOrg,
    };
  };
